import { Navigate } from 'react-router-dom';
import type { ReactElement } from 'react';
import { findLazyPage, lazyPage, type LazyPage } from './lazyPage';
import HomeV2 from '@/pages/HomeV2';
import { ExternalRedirect } from './ExternalRedirect';
import { loadPostContent } from '@/lib/blogContent';

// Lazy-load all non-Home routes for code splitting (preloadable, see lazyPage)
const Features = lazyPage(() => import('@/pages/Features'), 'src/pages/Features.tsx');
const Pricing = lazyPage(() => import('@/pages/Pricing'), 'src/pages/Pricing.tsx');
const ForRecruitingAgencies = lazyPage(() => import('@/pages/ForRecruitingAgencies'), 'src/pages/ForRecruitingAgencies.tsx');
const RecruitmentAutomation = lazyPage(() => import('@/pages/RecruitmentAutomation'), 'src/pages/RecruitmentAutomation.tsx');
const AIInterviews = lazyPage(() => import('@/pages/AIInterviews'), 'src/pages/AIInterviews.tsx');
const ResumeScreening = lazyPage(() => import('@/pages/ResumeScreening'), 'src/pages/ResumeScreening.tsx');
const WorkStyleAssessment = lazyPage(() => import('@/pages/WorkStyleAssessment'), 'src/pages/WorkStyleAssessment.tsx');
const About = lazyPage(() => import('@/pages/About'), 'src/pages/About.tsx');
const Contact = lazyPage(() => import('@/pages/Contact'), 'src/pages/Contact.tsx');
const PrivacyPolicy = lazyPage(() => import('@/pages/PrivacyPolicy'), 'src/pages/PrivacyPolicy.tsx');
const TermsOfService = lazyPage(() => import('@/pages/TermsOfService'), 'src/pages/TermsOfService.tsx');
const Security = lazyPage(() => import('@/pages/Security'), 'src/pages/Security.tsx');
const WhatIsAIRecruitingAgent = lazyPage(() => import('@/pages/WhatIsAIRecruitingAgent'), 'src/pages/WhatIsAIRecruitingAgent.tsx');
const McpServer = lazyPage(() => import('@/pages/McpServer'), 'src/pages/McpServer.tsx');
const RecruitingMcpServers = lazyPage(() => import('@/pages/RecruitingMcpServers'), 'src/pages/RecruitingMcpServers.tsx');
const BestAiRecruitingSoftware = lazyPage(() => import('@/pages/BestAiRecruitingSoftware'), 'src/pages/BestAiRecruitingSoftware.tsx');
const RecruitmentAgencySoftware = lazyPage(() => import('@/pages/RecruitmentAgencySoftware'), 'src/pages/RecruitmentAgencySoftware.tsx');
const RecruitmentSoftwarePricing = lazyPage(() => import('@/pages/RecruitmentSoftwarePricing'), 'src/pages/RecruitmentSoftwarePricing.tsx');
const PricingGuide = lazyPage(() => import('@/pages/PricingGuide'), 'src/pages/PricingGuide.tsx');
const AtsMcpGuide = lazyPage(() => import('@/pages/AtsMcpGuide'), 'src/pages/AtsMcpGuide.tsx');
const AgencyStartGuide = lazyPage(() => import('@/pages/AgencyStartGuide'), 'src/pages/AgencyStartGuide.tsx');
// Slugs of the ATS MCP guides in src/data/atsMcpGuides.ts (listed here so the data stays out of the main bundle).
const ATS_MCP_GUIDE_SLUGS = ['greenhouse-mcp', 'ashby-mcp', 'workable-mcp', 'workday-mcp', 'bullhorn-mcp', 'lever-mcp'];
const ClaudeIntegration = lazyPage(() => import('@/pages/AssistantIntegration').then(module => ({ default: module.ClaudeIntegration })), 'src/pages/AssistantIntegration.tsx');
const ChatGptIntegration = lazyPage(() => import('@/pages/AssistantIntegration').then(module => ({ default: module.ChatGptIntegration })), 'src/pages/AssistantIntegration.tsx');
const GeminiIntegration = lazyPage(() => import('@/pages/AssistantIntegration').then(module => ({ default: module.GeminiIntegration })), 'src/pages/AssistantIntegration.tsx');
const Integrations = lazyPage(() => import('@/pages/Integrations'), 'src/pages/Integrations.tsx');
const Resources = lazyPage(() => import('@/pages/Resources'), 'src/pages/Resources.tsx');
const Templates = lazyPage(() => import('@/pages/Templates'), 'src/pages/Templates.tsx');
const TemplateDetail = lazyPage(() => import('@/pages/TemplateDetail'), 'src/pages/TemplateDetail.tsx');
const InterviewQuestions = lazyPage(() => import('@/pages/InterviewQuestions'), 'src/pages/InterviewQuestions.tsx');
const RoleQuestions = lazyPage(() => import('@/pages/InterviewQuestions').then(module => ({ default: module.RoleQuestionsRoute })), 'src/pages/InterviewQuestions.tsx');
const Faq = lazyPage(() => import('@/pages/Faq'), 'src/pages/Faq.tsx');
const OneWayVideoInterviews = lazyPage(() => import('@/pages/OneWayVideoInterviews'), 'src/pages/OneWayVideoInterviews.tsx');
const CandidateScreeningSoftware = lazyPage(() => import('@/pages/CandidateScreeningSoftware'), 'src/pages/CandidateScreeningSoftware.tsx');
const Glossary = lazyPage(() => import('@/pages/Glossary'), 'src/pages/Glossary.tsx');
const ScreeningCostCalculatorPage = lazyPage(() => import('@/pages/ScreeningCostCalculatorPage'), 'src/pages/ScreeningCostCalculatorPage.tsx');
const AtsIntegration = lazyPage(() => import('@/pages/AtsIntegration'), 'src/pages/AtsIntegration.tsx');
const PermanentRecruitment = lazyPage(() => import('@/pages/DeskSolution').then(module => ({ default: module.PermanentRecruitment })), 'src/pages/DeskSolution.tsx');
const ContractTempStaffing = lazyPage(() => import('@/pages/DeskSolution').then(module => ({ default: module.ContractTempStaffing })), 'src/pages/DeskSolution.tsx');
const ExecutiveSearch = lazyPage(() => import('@/pages/DeskSolution').then(module => ({ default: module.ExecutiveSearch })), 'src/pages/DeskSolution.tsx');
const RpoEmbeddedTeams = lazyPage(() => import('@/pages/DeskSolution').then(module => ({ default: module.RpoEmbeddedTeams })), 'src/pages/DeskSolution.tsx');
const Alternatives = lazyPage(() => import('@/pages/Alternatives'), 'src/pages/Alternatives.tsx');
const AlternativeDetail = lazyPage(() => import('@/pages/AlternativeDetail'), 'src/pages/AlternativeDetail.tsx');
const Malaysia = lazyPage(() => import('@/pages/Malaysia'), 'src/pages/Malaysia.tsx');
const My = lazyPage(() => import('@/pages/My'), 'src/pages/My.tsx');
const Blog = lazyPage(() => import('@/pages/Blog'), 'src/pages/Blog.tsx');
const BlogSlugResolver = lazyPage(() => import('@/pages/BlogSlugResolver'), 'src/pages/BlogSlugResolver.tsx');
const NotFound = lazyPage(() => import('@/pages/NotFound'), 'src/pages/NotFound.tsx');

/** Route table: a lazy page, or any other element (eager page, redirect). */
export const ROUTES: Array<[string, LazyPage | ReactElement]> = [
  ['/', <HomeV2 />],
  ['/legacy', <Navigate to="/" replace />],
  ['/features', Features],
  ['/pricing', Pricing],
  ['/for-recruiting-agencies', ForRecruitingAgencies],
  ['/recruitment-automation', RecruitmentAutomation],
  ['/ai-interviews', AIInterviews],
  ['/resume-screening', ResumeScreening],
  ['/work-style-assessment', WorkStyleAssessment],
  ['/about', About],
  ['/trust/evidence', <Navigate to="/about" replace />],
  ['/contact', Contact],
  ['/privacy-policy', PrivacyPolicy],
  ['/terms-of-service', TermsOfService],
  ['/security', Security],
  ['/guides/what-is-an-ai-recruiting-agent', WhatIsAIRecruitingAgent],
  ['/guides/recruiting-mcp-servers', RecruitingMcpServers],
  ['/guides/best-ai-recruiting-software', BestAiRecruitingSoftware],
  ['/guides/recruitment-agency-software', RecruitmentAgencySoftware],
  ['/guides/recruitment-software-pricing', RecruitmentSoftwarePricing],
  ...ATS_MCP_GUIDE_SLUGS.map((slug): [string, LazyPage] => [`/guides/${slug}`, AtsMcpGuide]),
  ['/guides/how-to-start-a-staffing-agency', AgencyStartGuide],
  ['/guides/how-to-start-a-recruitment-agency-uk', AgencyStartGuide],
  ['/guides/recruitment-agency-fees', AgencyStartGuide],
  // Vendor pricing guides (/guides/bullhorn-pricing …); keep after every fixed /guides/ route.
  ['/guides/:guide', PricingGuide],
  ['/mcp', McpServer],
  ['/integrations', Integrations],
  ['/resources', Resources],
  ['/templates', Templates],
  ['/templates/:template', TemplateDetail],
  ['/interview-questions', InterviewQuestions],
  ['/interview-questions/:role', RoleQuestions],
  ['/faq', Faq],
  ['/one-way-video-interviews', OneWayVideoInterviews],
  ['/candidate-screening-software', CandidateScreeningSoftware],
  ['/glossary', Glossary],
  ['/screening-cost-calculator', ScreeningCostCalculatorPage],
  ['/integrations/claude', ClaudeIntegration],
  ['/integrations/chatgpt', ChatGptIntegration],
  ['/integrations/gemini', GeminiIntegration],
  ['/integrations/:ats', AtsIntegration],
  ['/solutions/permanent-recruitment', PermanentRecruitment],
  ['/solutions/contract-temp-staffing', ContractTempStaffing],
  ['/solutions/executive-search', ExecutiveSearch],
  ['/solutions/rpo-embedded-teams', RpoEmbeddedTeams],
  ['/alternatives', Alternatives],
  ['/alternatives/:platform', AlternativeDetail],
  ['/documentation', <ExternalRedirect to="https://platform.beatview.ai/docs" />],
  ['/documentation/introduction', <ExternalRedirect to="https://platform.beatview.ai/docs" />],
  ['/privacy', <Navigate to="/privacy-policy" replace />],
  ['/terms', <Navigate to="/terms-of-service" replace />],
  ['/malaysia', Malaysia],
  ['/my', My],
  ['/blog', Blog],
  ['/blog/:slug', BlogSlugResolver],
  ['/home', <Navigate to="/" replace />],
  ['*', NotFound],
];

export const isLazyPage = (target: LazyPage | ReactElement): target is LazyPage => typeof target === 'function';

/** Loads the code for the page at `pathname` so it can render without suspending. Returns its module path. */
export async function preloadRoute(pathname: string): Promise<string | null> {
  const page = findLazyPage(ROUTES.map(([path, target]) => [path, isLazyPage(target) ? target : null]), pathname);
  if (!page) return null;
  // A blog post also needs its article body, so the pre-built HTML and the first client render match.
  const postSlug = pathname.match(/^\/blog\/([a-z0-9-]+)\/?$/)?.[1];
  await Promise.all([page.preload(), postSlug ? loadPostContent(postSlug) : null]);
  return page.source;
}
